import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { Point, CategorieMarqueur } from '../../types/map';
import { renderTemplate, renderFallback, buildCssVariables } from './templateRenderer';
import { CloseIcon } from '../icons/CloseIcon';
import './MarkerInfo.css';

interface MarkerPanneauProps {
  point: Point;
  categorie?: CategorieMarqueur;
  onClose: () => void;
}

/**
 * Panneau latéral coulissant (droite) affichant le contenu du template.
 * Fermeture par touche Echap ou bouton de fermeture.
 * Animation d'ouverture via classe CSS --open.
 * 
 * Les CSS Variables sont injectées sur le wrapper pour permettre
 * l'utilisation de var(--couleur), var(--notation), etc. dans le CSS.
 */
export const MarkerPanneau: React.FC<MarkerPanneauProps> = ({ point, categorie, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const panneauRef = useRef<HTMLDivElement>(null);

  const html = categorie?.templateHtml
    ? renderTemplate(categorie.templateHtml, point, categorie)
    : renderFallback(point);

  const scopeClass = categorie?.id ? `tpl-cat-${categorie.id}` : '';
  const sizeClass = `marker-panneau--${categorie?.largeurAffichage || 'medium'}`;
  const cssVars = categorie ? buildCssVariables(point, categorie) : {};

  // Build container style from styleConteneur
  const sc = categorie?.styleConteneur;
  const containerStyle: Record<string, string> = {};
  if (sc?.borderRadius) containerStyle['--ctn-radius'] = sc.borderRadius;
  if (sc?.padding) containerStyle['--ctn-padding'] = sc.padding;
  if (sc?.background) containerStyle['--ctn-bg'] = sc.background;
  else if (categorie?.couleur) containerStyle['--ctn-bg'] = categorie.couleur;
  if (sc?.closeButtonColor) containerStyle['--ctn-close-color'] = sc.closeButtonColor;
  if (sc?.closeButtonHoverColor) containerStyle['--ctn-close-hover-color'] = sc.closeButtonHoverColor;
  if (sc?.boxShadow) containerStyle['--ctn-shadow'] = sc.boxShadow;

  // Animation d'ouverture
  useEffect(() => {
    // requestAnimationFrame pour que le navigateur applique d'abord translateX(100%)
    requestAnimationFrame(() => {
      setIsOpen(true);
    });
    panneauRef.current?.focus();
  }, []);

  // Echap pour fermer
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleClose = () => {
    setIsOpen(false);
    // Attendre la fin de la transition CSS avant de fermer réellement
    setTimeout(onClose, 300);
  };

  return (
    <div
      ref={panneauRef}
      className={`marker-panneau ${sizeClass} ${isOpen ? 'marker-panneau--open' : ''}`}
      tabIndex={-1}
      role="complementary"
      aria-label={`Détails du point : ${point.titre || point.nom}`}
      style={containerStyle as React.CSSProperties}
    >
      <div className="marker-panneau__header">
        <h3 className="marker-panneau__title">{point.titre || point.nom}</h3>
        <button
          className="marker-panneau__close"
          onClick={handleClose}
          aria-label="Fermer le panneau"
          title="Fermer"
        >
          <CloseIcon size={16} />
        </button>
      </div>
      <div className="marker-panneau__content">
        <div
          className={scopeClass}
          style={cssVars}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
};
